import { Lock, Check, Database, AlertTriangle, RefreshCw } from 'lucide-react';

interface MeuPerfilGeralTabProps {
    user: any;
    onOpenPasswordModal: () => void;
    onLinkGoogle: () => void;
    onLinkDiscord: () => void;
    importHistoricoStep: 'idle' | 'confirm' | 'running' | 'done' | 'error';
    importHistoricoProgress: { processed: number; total: number } | null;
    onImportHistoricoConfirm: () => void;
    onImportHistoricoRun: () => void;
    onImportHistoricoReset: () => void;
}

export function MeuPerfilGeralTab({
    user,
    onOpenPasswordModal,
    onLinkGoogle,
    onLinkDiscord,
    importHistoricoStep,
    importHistoricoProgress,
    onImportHistoricoConfirm,
    onImportHistoricoRun,
    onImportHistoricoReset,
}: MeuPerfilGeralTabProps) {
    return (
        <>
            {/* Contas Vinculadas — full width, cards already in 2-col on desktop */}
            <section>
                <h2 className="mb-4 text-lg font-bold text-white">Contas Vinculadas</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="flex items-center justify-between rounded-lg border border-white/5 bg-white/5 p-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#5865F2]/20 text-[#5865F2]">
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C2.451 6.018 1.99 7.765 1.99 9.54c0 5.089 3.158 9.417 7.18 11.174a.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028c4.022-1.757 7.18-6.085 7.18-11.174 0-1.775-.46-3.522-1.648-5.142a.066.066 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-white">Discord</h3>
                                <p className="text-xs text-white/50">{user.usr_discord_id ? 'Vinculado' : 'Não vinculado'}</p>
                            </div>
                        </div>
                        {user.usr_discord_id ? (
                            <div className="flex items-center gap-1.5 rounded border border-green-500/20 bg-green-500/10 px-3 py-1.5 text-xs font-medium text-green-500">
                                <Check size={14} /> OK
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={onLinkDiscord}
                                className="rounded bg-[#5865F2] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#4752C4]"
                            >
                                Vincular
                            </button>
                        )}
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-white/5 bg-white/5 p-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white">
                                <svg className="h-5 w-5" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-white">Google</h3>
                                <p className="text-xs text-white/50">{user.usr_google_id ? 'Vinculado' : 'Não vinculado'}</p>
                            </div>
                        </div>
                        {user.usr_google_id ? (
                            <div className="flex items-center gap-1.5 rounded border border-green-500/20 bg-green-500/10 px-3 py-1.5 text-xs font-medium text-green-500">
                                <Check size={14} /> OK
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={onLinkGoogle}
                                className="rounded bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/20"
                            >
                                Vincular
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Segurança + Dados — stacked on mobile, side-by-side on desktop */}
            <div className="mt-8 border-t border-white/5 pt-8 md:grid md:grid-cols-2 md:gap-8">
                <section>
                    <h2 className="mb-4 text-lg font-bold text-white">Segurança</h2>
                    <div className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 p-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/20 text-orange-500">
                                <Lock size={20} />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white">Sua Senha</h3>
                                <p className="text-xs font-medium text-white/40">Mantenha sua conta protegida</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onOpenPasswordModal}
                            className="ml-3 shrink-0 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.1em] text-white shadow-lg transition-all hover:bg-white/10 active:scale-95"
                        >
                            Alterar Senha
                        </button>
                    </div>
                </section>

                <section className="mt-8 md:mt-0">
                    <h2 className="mb-4 text-lg font-bold text-white">Dados</h2>
                    <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/20 text-blue-400">
                                <Database size={20} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-bold text-white">Importar histórico de leitura</h3>
                                <p className="mt-0.5 text-xs text-white/40">
                                    Traz estado de leitura e capítulos lidos da conta antiga. Favoritos e reações não são alterados.
                                </p>

                                {importHistoricoStep === 'idle' && (
                                    <button
                                        type="button"
                                        onClick={onImportHistoricoConfirm}
                                        className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-black uppercase tracking-[0.1em] text-white shadow-lg transition-all hover:bg-white/10 active:scale-95"
                                    >
                                        Importar
                                    </button>
                                )}

                                {importHistoricoStep === 'confirm' && (
                                    <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
                                        <div className="mb-2 flex items-center gap-2 text-amber-400">
                                            <AlertTriangle size={14} />
                                            <span className="text-xs font-semibold">Atenção</span>
                                        </div>
                                        <p className="mb-3 text-xs text-amber-200/70">
                                            Todo o histórico de leitura atual será substituído pelos dados da conta antiga. Esta ação não pode ser desfeita.
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={onImportHistoricoRun}
                                                className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-400"
                                            >
                                                Confirmar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onImportHistoricoReset}
                                                className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:bg-white/10"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {importHistoricoStep === 'running' && (
                                    <div className="mt-3">
                                        <div className="mb-1.5 flex items-center gap-2 text-xs text-white/60">
                                            <RefreshCw size={12} className="animate-spin" />
                                            <span>
                                                Importando{importHistoricoProgress && importHistoricoProgress.total > 0
                                                    ? ` — ${importHistoricoProgress.processed} / ${importHistoricoProgress.total} obras`
                                                    : '...'}
                                            </span>
                                        </div>
                                        {importHistoricoProgress && importHistoricoProgress.total > 0 && (
                                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                                                <div
                                                    className="h-full rounded-full bg-blue-500 transition-all duration-300"
                                                    style={{ width: `${Math.min(100, Math.round((importHistoricoProgress.processed / importHistoricoProgress.total) * 100))}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {importHistoricoStep === 'done' && (
                                    <div className="mt-3 flex items-center gap-3">
                                        <span className="text-xs font-medium text-green-400">Importação concluída!</span>
                                        <button
                                            type="button"
                                            onClick={onImportHistoricoReset}
                                            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60 transition-colors hover:bg-white/10"
                                        >
                                            OK
                                        </button>
                                    </div>
                                )}

                                {importHistoricoStep === 'error' && (
                                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                                        <span className="text-xs font-medium text-red-400">Falha na importação.</span>
                                        <button
                                            type="button"
                                            onClick={onImportHistoricoRun}
                                            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60 transition-colors hover:bg-white/10"
                                        >
                                            Tentar novamente
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onImportHistoricoReset}
                                            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60 transition-colors hover:bg-white/10"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
